import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import CreateSupervisorModal from './CreateSupervisorModal';
import SupervisorDetailModal from './SupervisorDetailModal'; // SupervisorDetailModal import
import Swal from 'sweetalert2'; // SweetAlert2 임포트
import '../App.css'; // 공통 스타일 사용
import { useAuth } from '../context/AuthContext';
import './SupervisorPage.css';

function SupervisorPage() {
  const { user } = useAuth();
  const [supervisors, setSupervisors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false); // 상세 모달 상태
  const [selectedSupervisor, setSelectedSupervisor] = useState(null); // 선택된 감리원 정보
  const [isSupervisorDetailEditMode, setIsSupervisorDetailEditMode] = useState(false); // 상세 모달 편집 모드 상태
  const [newSupervisor, setNewSupervisor] = useState({
    name: '', position: '', birthDate: '', phone: '', email: '', notes: ''
  });

  const fetchSupervisors = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/supervisors');
      setSupervisors(response.data);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    }
  }, []);

  useEffect(() => {
    fetchSupervisors();
  }, [fetchSupervisors]);

  const filteredSupervisors = supervisors.filter(supervisor =>
    supervisor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supervisor.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supervisor.birthDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supervisor.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supervisor.email && supervisor.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (supervisor.notes && supervisor.notes.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openDetailModal = (supervisor, isEditMode = false) => {
    setSelectedSupervisor(supervisor);
    setIsSupervisorDetailEditMode(isEditMode);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setSelectedSupervisor(null);
    setIsSupervisorDetailEditMode(false);
    setIsDetailModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'birthDate') {
      // 숫자만 남기고 모든 문자 제거
      formattedValue = value.replace(/[^0-9]/g, '');
      // YYYY-MM-DD 형식으로 하이픈 추가
      if (formattedValue.length > 4 && formattedValue.length <= 6) {
        formattedValue = formattedValue.replace(/(\d{4})(\d+)/, '$1-$2');
      } else if (formattedValue.length > 6) {
        formattedValue = formattedValue.replace(/(\d{4})(\d{2})(\d+)/, '$1-$2-$3');
      }
    } else if (name === 'phone') {
      // 숫자만 남기고 모든 문자 제거
      formattedValue = value.replace(/[^0-9]/g, '');
      // 전화번호 형식으로 하이픈 추가 (010-XXXX-XXXX 또는 02-XXXX-XXXX 등)
      if (formattedValue.startsWith('02')) { // 서울 지역번호
        if (formattedValue.length > 2 && formattedValue.length <= 6) {
          formattedValue = formattedValue.replace(/(\d{2})(\d+)/, '$1-$2');
        } else if (formattedValue.length > 6) {
          formattedValue = formattedValue.replace(/(\d{2})(\d{4})(\d+)/, '$1-$2-$3');
        }
      } else { // 일반 휴대폰 번호
        if (formattedValue.length > 3 && formattedValue.length <= 7) {
          formattedValue = formattedValue.replace(/(\d{3})(\d+)/, '$1-$2');
        } else if (formattedValue.length > 7) {
          formattedValue = formattedValue.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
        }
      }
    }

    setNewSupervisor({ ...newSupervisor, [name]: formattedValue });
  };

  const handleAddSupervisor = async (e) => {
    e.preventDefault();

    // 프론트엔드 유효성 검사 (필수 필드 확인)
    if (!newSupervisor.name || !newSupervisor.position || !newSupervisor.birthDate || !newSupervisor.phone) {
      Swal.fire({
        icon: 'warning',
        title: '입력 오류',
        text: '이름, 직위, 생년월일, 연락처는 필수 항목입니다.',
      });
      return;
    }

    try {
      await axiosInstance.post('/supervisors', newSupervisor);
      fetchSupervisors(); // 감리원 추가 후 목록 다시 불러오기
      setNewSupervisor({ name: '', position: '', birthDate: '', phone: '' }); // 입력 필드 초기화
      closeModal(); // 모달 닫기
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData)) {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData.join('\n'),
          });
        } else if (typeof errorData === 'string') {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: '감리원 추가 중 오류가 발생했습니다.',
          });
        }
      }
    } finally {
      // Ensure modal closes even on error
      closeModal();
    }
  };

  const handleUpdateSupervisor = async (updatedSupervisor) => {
    try {
      await axiosInstance.put(`/supervisors/${updatedSupervisor.id}`, updatedSupervisor);
      fetchSupervisors();
      closeDetailModal();
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData)) {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData.join('\n'),
          });
        } else if (typeof errorData === 'string') {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: errorData,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: '오류',
            text: '감리원 수정 중 오류가 발생했습니다.',
          });
        }
      }
    }
  };

  const handleDeleteSupervisor = async (supervisorId) => {
    const result = await Swal.fire({
      title: '정말로 이 감리원을 삭제하시겠습니까?',
      text: "이 감리원을 삭제하면 되돌릴 수 없습니다!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '예, 삭제합니다!',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/supervisors/${supervisorId}`);
        Swal.fire(
          '삭제됨!',
          '감리원이 성공적으로 삭제되었습니다.',
          'success'
        );
        fetchSupervisors();
        closeDetailModal();
      } catch (error) {
        console.error('Error deleting supervisor:', error);
        Swal.fire(
          '오류!',
          '감리원 삭제 중 오류가 발생했습니다.',
          'error'
        );
      }
    }
  };

  return (
    <div className="container">
      <br/>
      <h2>감리원 관리</h2>
      <br/><br/><br/>
      <div className="page-actions">
        <input
          type="text"
          placeholder="감리원 검색..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="button-group">
          {user && user.role === 'ADMIN' && (
            <button onClick={openModal} className="register-button">감리원 등록</button>
          )}
        </div>
      </div>
      <br/><br/><br/>
      {filteredSupervisors.length > 0 ? (
        <table className="supervisor-table">
          <thead>
            <tr>
              <th className="col-sv-name">이름</th>
              <th className="col-sv-position">직위</th>
              <th className="col-sv-birthdate">생년월일</th>
              <th className="col-sv-phone">연락처</th>
              <th className="col-sv-email">이메일</th>
              {user && user.role === 'ADMIN' && <th className="col-sv-actions">관리</th>}
            </tr>
          </thead>
          <tbody>
            {filteredSupervisors.map((supervisor) => (
              <tr key={supervisor.id}>
                <td className="col-sv-name" onClick={() => openDetailModal(supervisor, false)} style={{ cursor: 'pointer' }}>{supervisor.name}</td>
                <td className="col-sv-position" onClick={() => openDetailModal(supervisor, false)} style={{ cursor: 'pointer' }}>{supervisor.position}</td>
                <td className="col-sv-birthdate" onClick={() => openDetailModal(supervisor, false)} style={{ cursor: 'pointer' }}>{supervisor.birthDate}</td>
                <td className="col-sv-phone" onClick={() => openDetailModal(supervisor, false)} style={{ cursor: 'pointer' }}>{supervisor.phone}</td>
                <td className="col-sv-email" onClick={() => openDetailModal(supervisor, false)} style={{ cursor: 'pointer' }}>{supervisor.email}</td>
                {user && user.role === 'ADMIN' && (
                  <td className="col-sv-actions">
                    <div className="supervisor-actions-container"> {/* New container for buttons */}
                      <button onClick={() => openDetailModal(supervisor, true)} className="supervisor-edit-button">수정</button>
                      <button onClick={() => handleDeleteSupervisor(supervisor.id)} className="supervisor-delete-button">삭제</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>등록된 감리원이 없습니다.</p>
      )}

      <CreateSupervisorModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onAddSupervisor={handleAddSupervisor}
        newSupervisor={newSupervisor}
        onInputChange={handleInputChange}
      />

      {selectedSupervisor && (
        <SupervisorDetailModal
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          supervisor={selectedSupervisor}
          onUpdateSupervisor={handleUpdateSupervisor}
          onDeleteSupervisor={handleDeleteSupervisor}
          isEditMode={isSupervisorDetailEditMode} // isEditMode prop 전달
        />
      )}
    </div>
  );
}

export default SupervisorPage;